import { ObjectId } from "mongodb";
import { getPropDescription } from "./PropUtils";

/** 炼金道具 */
export interface Props {
    /** 数据ID */
    _id: string | ObjectId;
    /** 名称 */
    name: string;
    /** 资源ID */
    assetId: string;
    /** 类型 */
    type: number;
    /** 品质 */
    rarity: number;
    /** 标签 */
    tags: number[];
    /** 衍生物（不可抽取） */
    derived?: boolean;
    /** 每回合产出 */
    yield: number;
    /** 词条 */
    entries: Entry[];
}

/** 被执行词条操作的目标 */
export type Target = {
    /** 执行目标，self：自身，triggers：触发器选中的目标 */
    target: 'self' | 'triggers'
} | ({
    /** 执行目标，select：选择目标 */
    target: 'select'
} & Scope & TargetSelect)

/** 作用范围 */
export interface Scope {
    /** 作用范围，neighbor：相邻，row：同行，column：同列，diagonal：对角线上的，global：全场 */
    scope: 'neighbor' | 'row' | 'column' | 'diagonal' | 'global'
}

/** 概率 */
export interface Chance {
    /** 触发概率[0-100]%，undefined/100：100% */
    chance: number
}

/** 位置 */
export interface Location {
    /** 位置，battle：战场，depot：材料库，corner：角落，empty：空位置 */
    location?: 'battle' | 'depot' | 'corner' | 'empty'
}

/** 次数 */
export interface Times {
    /** 次数 */
    times: number;
}

/** 数量 */
export interface Count {
    /** 数量 */
    count: number;
}

/** 转化时保持标签 */
export interface KeepTag {
    keepTag?: boolean;
}

/** 比较 */
export interface Compare {
    /** 取值来源，none：无，yield：取道具的永久产出值 */
    source: 'none' | 'yield';
    /** 比较方式，equal：等于，greaterOrEqual：大于等于，lessOrEqual：小于等于，greater：大于，less：小于 */
    term?: 'equal' | 'greaterOrEqual' | 'lessOrEqual' | 'greater' | 'less';
    /** 目标值 */
    dest?: number;
}

/** 逻辑(多个值之间的关系)，or：或，and：和 */
export interface Logic {
    logic?: 'or' | 'and'
}

/** 索引数组 */
export interface Indexes {
    /** 索引数组 */
    indexes: number[]
}

/** 产出调整 */
export type YieldChange = {
    /** 计算方式，add：加，multiple：乘 */
    method: 'add' | 'multiply';
    /** 值 */
    value: number;
} | {
    /** 计算方式，random：随机 */
    method: 'random';
    /** 最小值(含) */
    min: number;
    /** 最大值(含) */
    max: number;
}

/** 品质权重调整 */
export type WeightChange = {
    /** 目标品质 */
    raritiesToChange: number[],
    /** 倍率 */
    weightMultiplier: number
}

type _TargetSelect = {
    /** 筛选字段，prop：道具 */
    field: 'prop'
    /** 目标道具ID，仅在field为prop时有效 */
    props?: string[];
    /** 数量限制，undifined/0：无限制，-1：仅触发器有效，反向筛选，即没筛选到目标时触发 */
    limit?: number;
} | {
    /** 筛选字段，tag：标签 */
    field: 'tag'
    /** 标签目标，仅在field为tag时有效 */
    tags?: number[];
    /** 数量限制，undifined/0：无限制，-1：仅触发器有效，反向筛选，即没筛选到目标时触发 */
    limit?: number;
} | {
    /** 筛选字段，rarity：品质 */
    field: 'rarity'
    /** 品质目标，仅在field为rarity时有效 */
    rarities?: number[];
    /** 数量限制，0：没有数量限制，-1：没筛选到目标时触发 */
    limit?: number;
} | {
    /** 筛选字段，any：任意 */
    field: 'any';
    /** 数量限制，0：没有数量限制，-1：没筛选到目标时触发 */
    limit?: number;
} | {
    /** 筛选字段，same：场上存在指定数量的相同道具 */
    field: 'same',
    /** 数量限制 */
    limit: number
}

/** 目标筛选 */
export type TargetSelect = _TargetSelect & Compare & Logic;

/** 道具词条MAP */
export interface EntryMap {
    /** 消除 */
    Remove: Target & Chance;
    /** 添加 */
    Add: Location & Chance & TargetSelect;
    /** 转化 */
    Replace: Target & Chance & TargetSelect & KeepTag;
    /** 额外产出 */
    ExtraYield: Target & Chance & YieldChange;
    /** 永久增加回合产出 */
    Grow: Target & Chance & Count;
    /** 收集 */
    Collect: Times & Scope & TargetSelect & Count;
    /** 收获 */
    Gain: {};
    /** 消除特性，indexes为undefined或空数组时，消除目标全部特性，否则消除指定索引的特性 */
    RemoveEntry: TargetSelect & Indexes;
    /** 品质权重调整 */
    DrawWeightChange: WeightChange;
    /** 保护，保护指定材料不被消除 */
    Protect: Scope & TargetSelect;
    /** 调和次数 */
    Refine: Count;
    /** 转移位置 */
    Transfer: {};
    /** 仅作为前置条件，触发下一词条，不触发任何操作 */
    Next: {};
    /** 倒计时增减 */
    Timer: Target & Count;
}

/** 道具词条 */
export type Entry = {
    [K in keyof EntryMap]: { type: K, trigger: Trigger } & EntryMap[K]
}[keyof EntryMap]

/** 触发器MAP */
export interface TriggerMap {
    /** 每X回合触发，count：触发需要的回合数 */
    EachTurns: Count & {
        /** 循环次数，undefined/0：无限循环 */
        loops?: number;
    };
    /** 上一词条后触发 */
    AfterPrev: {};
    /** 每次消除时触发 */
    EachRemove: {};
    /** 被消除时触发 */
    BeenRemoved: {};
    /** 被转化时触发 */
    BeenReplaced: {};
    /** 关联道具时触发 */
    RelativeProp: Scope & TargetSelect;
    /** 使用时触发，具有此词条的道具可被主动使用，count：可使用次数，undefined/0代表可无限使用 */
    BeenUsed: Count;
    /** 位于指定位置时触发 */
    Located: Location;
    /** 目标被消除时触发 */
    TargetBeenRemoved: Scope & TargetSelect;
    /** 上一词条每次触发时 */
    EachTriggered: {};
}

/** 触发器 */
export type Trigger = {
    [K in keyof TriggerMap]: { type: K } & TriggerMap[K]
}[keyof TriggerMap]

/**
 * 获取道具词条描述文本
 * @param props 道具
 * @param all 所有道具
 * @returns 道具词条描述文本
 */
export const getDescription = getPropDescription;
